import { useEffect, useRef } from 'react';
import NoSleep from 'nosleep.js';

type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
};

type WakeLockApi = {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
};

function getWakeLockApi(): WakeLockApi | null {
  if (typeof navigator === 'undefined') return null;
  const api = (navigator as unknown as { wakeLock?: WakeLockApi }).wakeLock;
  return api ?? null;
}

// Shared NoSleep instance — NoSleep attaches a single hidden <video> to the DOM
// and starts/stops playback. Refcount so multiple consumers can coexist.
let sharedNoSleep: NoSleep | null = null;
let noSleepRefCount = 0;

function acquireNoSleep() {
  if (typeof document === 'undefined') return;
  if (!sharedNoSleep) sharedNoSleep = new NoSleep();
  noSleepRefCount += 1;
  if (noSleepRefCount === 1) {
    const p = sharedNoSleep.enable();
    // enable() returns a promise in newer versions; swallow rejection if the
    // browser blocks autoplay (user gesture is usually present since audio
    // playback is already running).
    if (p && typeof (p as Promise<unknown>).catch === 'function') {
      (p as Promise<unknown>).catch(() => undefined);
    }
  }
}

function releaseNoSleep() {
  if (!sharedNoSleep) return;
  noSleepRefCount = Math.max(0, noSleepRefCount - 1);
  if (noSleepRefCount === 0) {
    try {
      sharedNoSleep.disable();
    } catch {
      // ignore
    }
  }
}

/**
 * Hold a Screen Wake Lock while `active` is true so the device screen
 * stays awake during audio playback. Automatically re-acquires the lock
 * when the tab becomes visible again (the browser releases locks on hide).
 *
 * On browsers that do not expose `navigator.wakeLock` (e.g. Huawei Browser
 * and older WebView builds), falls back to NoSleep.js, which plays a silent
 * looping video to keep the compositor active and prevent screen auto-lock.
 */
export function useScreenWakeLock(active: boolean) {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const activeRef = useRef(active);
  const fallbackAcquiredRef = useRef(false);

  useEffect(() => {
    activeRef.current = active;

    const wakeLockApi = getWakeLockApi();
    const useFallback = !wakeLockApi;

    let cancelled = false;

    const release = async () => {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      if (sentinel && !sentinel.released) {
        try {
          await sentinel.release();
        } catch {
          // ignore — sentinel may already be released by the browser
        }
      }
      if (fallbackAcquiredRef.current) {
        fallbackAcquiredRef.current = false;
        releaseNoSleep();
      }
    };

    const acquire = async () => {
      if (!activeRef.current) return;
      if (document.visibilityState !== 'visible') return;

      if (useFallback || !wakeLockApi) {
        if (!fallbackAcquiredRef.current) {
          fallbackAcquiredRef.current = true;
          acquireNoSleep();
        }
        return;
      }

      if (sentinelRef.current && !sentinelRef.current.released) return;
      try {
        const sentinel = await wakeLockApi.request('screen');
        if (cancelled || !activeRef.current) {
          sentinel.release().catch(() => undefined);
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch {
        // Browser may reject (permissions, low battery, non-HTTPS) — try
        // NoSleep as a last-resort fallback before giving up.
        if (!fallbackAcquiredRef.current) {
          fallbackAcquiredRef.current = true;
          acquireNoSleep();
        }
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeRef.current) {
        acquire();
      }
    };

    if (active) {
      acquire();
    } else {
      release();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [active]);
}

export default useScreenWakeLock;
